<template>
  <div class="w-full flex flex-col">
    <div class="w-full flex items-center p-1 bg-neutral-25 mb-4" style="border-radius: 10px;">
      <div class="w-8 h-8 flex items-center justify-center">
        <AdRankIcon class="text-icon-muted" />
      </div>
      <BaseText type="label" size="sm" class="text-text-muted ml-1.5">
        Ad Rank
      </BaseText>
      <BaseText type="body" size="sm" class="text-text-normal ml-3">
        Compare ads by their relative share of total impressions in this brand's Meta library.
      </BaseText>
    </div>
    <AdvertiserSelectionDropdown
      v-if="brand?.id && advertiserOptions.length > 1 && !locked"
      class="mb-4 fade-in z-30"
      :selected-advertiser-id.sync="selectedAdvertiserId"
      :advertiser-options="advertiserOptions"
    />
    <AdRankingGraph
      class="z-20"
      :brand="brand"
      :page-id="selectedAdvertiserId"
      @show-info-modal="showRankInformationModal = true"
    />
    <div class="w-full grid grid-cols-3 gap-x-1 mt-3 p-0.5 rounded-xl bg-neutral-25 z-10" style="height: 88px">
      <div v-for="(category, index) in adRankCategories" :key="`category-${category.key}`" class="w-full flex items-center">
        <div v-if="index > 0" class="mr-1 border-l border-border-normal" style="height: 64px" />
        <BaseTooltipV2
          class="w-full"
          :tooltip-enabled="category.count === 0 || category.locked"
          :text="category.locked ? 'Track this brand to unlock more categories' : 'No ads in this category'"
          position="cursor"
          offset="6"
          :delay="500"
        >
          <button class="category-atom"
          :class="{
            'enabled': category.count > 0 && !category.locked,
            'active': selectedCategory === category.key,
            'first': index === 0,
            'last': index === adRankCategories.length - 1,
            'opacity-60': category.locked
          }"
          :disabled="category.count === 0 || category.locked"
          @click="handleChangeCategory(category.key)">
            <!-- Active Indicator -->
            <div class="active-indicator"><div class="inner"/></div>
            <!-- Label -->
            <div class="flex items-center gap-1 pl-1 py-1 pr-2 rounded-md z-10" 
            :style="{ color: category.labelColors.text, backgroundColor: category.labelColors.background }">
              <component :is="category.icon" />
              <BaseText type="label" size="sm">
                {{ category.label }}
              </BaseText>
            </div>
            <!-- Counts & Change -->
            <div class="flex items-center z-10">
              <div v-if="loadingInitialFetch" class="skeleton h-6 w-12 rounded-md" />
              <BaseText v-else type="heading" size="sm" class="pl-1 text-text-muted fade-in">
                {{ category.count.toLocaleString() }}
              </BaseText>
              <div v-if="loadingInitialFetch" class="skeleton h-4 w-24 ml-2.5 rounded-md" />
              <BaseText v-else-if="category.periodDiff === 0" type="body" size="xs" class="ml-3.5 text-text-normal fade-in">
                No change since last week
              </BaseText>
              <div v-else class="ml-2.5 flex items-center fade-in">
                <PeriodDiffIcon class="transform text-icon-normal" :style="{ transform: category.periodDiff >= 0 ? 'scaleY(1)' : 'scaleY(-1)' }"/>
                <BaseText type="label" size="xs" class="ml-1 text-text-normal">
                  {{ category.periodDiff >= 0 ? '+' : '' }}{{ category.periodDiff.toLocaleString() }}
                </BaseText>
                <BaseText type="body" size="xs" class="ml-1.5 text-text-normal">
                  since last week
                </BaseText>
              </div>
            </div>
          </button>
        </BaseTooltipV2>
      </div>
    </div>
    <div class="relative w-full mt-3 z-10" :class="{ 'overflow-hidden': locked }"
    :style="{ maxHeight: locked && rankedAds.length ? 'calc(100vh - 100px)' : 'auto' }">
      <AdvertisementList
        v-if="rankedAds.length"
        discovery
        :advertisements="rankedAds"
        :loading="loadingRankedAds"
        :all-ads-loaded="allAdsLoaded"
        hide-creative-count
        show-spyder-ad-rank
        :lock-ads="locked"
        @getMoreAds="getMoreRankedAds"
        @updateAds="updateRankedAdsLocal"
      />
      <AdsLockedOverlay 
        v-if="locked && rankedAds.length"
        class="absolute bottom-0 left-0 right-0 z-10" style="height: 500px"
        :title="`Start Tracking ${brand?.name || 'this brand'} to see Ranked Ads`"
        description="See this brand's top impression ads and how their rankings have changed over time."
      />
    </div>
    <div v-if="loadingRankedAds && !rankedAds.length" class="flex flex-col items-center gap-2 mt-24">
      <ScrapingLoadingIcon :width="80" :height="80" />
      <BaseText type="label" size="sm" class="text-text-subdued">
        Pulling the latest rankings
      </BaseText>
    </div>
    <div v-else-if="!rankedAds.length && !loadingInitialFetch" class="flex flex-col items-center mt-24 fade-in">
      <TrophyIcon class="text-icon-normal" />
      <BaseText type="label" size="sm" class="text-text-normal mt-3">
        No Rank Data Available
      </BaseText>
      <BaseText type="body" size="sm" class="text-text-normal mt-1.5 text-center" style="max-width: 240px;">
        This brand has no active ads that are currently ranked.
      </BaseText>
    </div>
    <infinite-loading
      v-if="rankedAds.length && !allAdsLoaded && !locked"
      :identifier="infiniteId"
      @infinite="getMoreRankedAds"
    >
      <div slot="spinner">
        <BaseLoadingLogo :margin="2" />
      </div>
      <div slot="no-more" />
      <div slot="no-results" />
    </infinite-loading>
    <RankInformationModal v-if="showRankInformationModal" @close="showRankInformationModal = false" />
  </div>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import InfiniteLoading from 'vue-infinite-loading'
import { getBrandMetaAdvertiserOptions } from '@/utils/spyder/brandUtils'

// Components
import AdvertiserSelectionDropdown from '../AdvertiserSelectionDropdown.vue'
import AdRankingGraph from '../adRanking/AdRankingGraph.vue'
import AdvertisementList from '@/components/advertisements/AdvertisementList.vue'
import RankInformationModal from '../adRanking/infoModal/RankInformationModal.vue'
import AdsLockedOverlay from '../AdsLockedOverlay.vue'

// Icons
import AdRankIcon from '@/components/globals/Icons/SpyderModeIcons/AdRankIcon.vue'
import PeriodDiffIcon from '@/components/globals/Icons/LensIcons/PeriodDiffIcon.vue'
import ScrapingLoadingIcon from '@/components/globals/Icons/SpyderV2Icons/ScrapingLoadingIcon.vue'
import TrophyIcon from '@/components/globals/Icons/TrophyIcon.vue'

// Category
import BreakoutIcon from '@/components/globals/Icons/SpyderV2Icons/BreakoutIcon.vue'
import EvergreenIcon from '@/components/globals/Icons/SpyderV2Icons/EvergreenIcon.vue'
import ScatterGraphIcon from '@/components/globals/Icons/LensIcons/graphs/ScatterGraphIcon.vue'

const ADS_PER_PAGE = 20
const EMPTY_COUNTS = { all: 0, breakout: 0, evergreen: 0 }

export default {
  name: 'AdRankBrandView',
  components: {
    AdvertiserSelectionDropdown,
    AdRankingGraph,
    AdvertisementList,
    RankInformationModal,
    AdsLockedOverlay,
    AdRankIcon,
    PeriodDiffIcon,
    ScrapingLoadingIcon,
    TrophyIcon,
    InfiniteLoading
  },
  props: {
    brand: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      selectedAdvertiserId: null,
      advertiserOptions: [],
      selectedCategory: 'all',
      showRankInformationModal: false,
      rankedAds: [],
      categoryCounts: {
        current: { ...EMPTY_COUNTS },
        previous: { ...EMPTY_COUNTS }
      },
      currentPage: 1,
      loadingInitialFetch: true,
      loadingRankedAds: false,
      allAdsLoaded: false,
      infiniteId: +new Date(),
      latestRequestId: 0
    }
  },
  watch: {
    brand: {
      handler (brand) {
        if (!brand?.id) return
        this.initAdvertiserOptions()
      },
      immediate: true
    },
    // Watch both request identifiers together so a brand switch only triggers a single fetch
    rankedAdsRequestKey: {
      handler () {
        this.resetRankedAdsState()
        this.fetchRankedAds()
      },
      immediate: true
    }
  },
  computed: {
    locked () {
      const isSubscribed = this.brand?.is_subscribed
      return isSubscribed === undefined ? false : !isSubscribed
    },
    rankedAdsRequestKey () {
      return `${this.brand?.id}-${this.selectedAdvertiserId}`
    },
    adRankCategories () {
      return [
        {
          key: 'all',
          label: 'Active Ads',
          icon: ScatterGraphIcon,
          count: this.categoryCounts.current.all,
          periodDiff: this.categoryCounts.current.all - this.categoryCounts.previous.all,
          labelColors: { text: '#2F3547', background: 'transparent' },
          isActive: this.selectedCategory === 'all',
          locked: false
        },
        {
          key: 'breakout',
          label: 'Breakout',
          icon: BreakoutIcon,
          count: this.categoryCounts.current.breakout,
          periodDiff: this.categoryCounts.current.breakout - this.categoryCounts.previous.breakout,
          labelColors: { text: '#002583', background: '#EEF8FF' },
          isActive: this.selectedCategory === 'breakout',
          locked: this.locked
        },
        {
          key: 'evergreen',
          label: 'Evergreen',
          icon: EvergreenIcon,
          count: this.categoryCounts.current.evergreen,
          periodDiff: this.categoryCounts.current.evergreen - this.categoryCounts.previous.evergreen,
          labelColors: { text: '#224F0B', background: '#F2FEEE' },
          isActive: this.selectedCategory === 'evergreen',
          locked: this.locked
        }
      ]
    }
  },
  methods: {
    initAdvertiserOptions () {
      if (!this.brand?.id) return
      const metaAdvertiserOptions = getBrandMetaAdvertiserOptions(this.brand)
      this.advertiserOptions = metaAdvertiserOptions
      this.selectedAdvertiserId = metaAdvertiserOptions?.find(option => option.isPrimary)?.advertiserId
        ?? metaAdvertiserOptions?.[0]?.advertiserId ?? null
    },
    async fetchRankedAds (category = 'all', page = 1) {
      // Any in-flight request is now stale, since the advertiser, category or page it was made for has changed
      const requestId = ++this.latestRequestId
      if (!this.brand?.id) return
      // Rankings are scoped to a single Meta advertiser, so we can't fetch them for brands without one
      if (!this.selectedAdvertiserId) {
        this.loadingRankedAds = false
        this.loadingInitialFetch = false
        this.allAdsLoaded = true
        return
      }
      this.loadingRankedAds = true
      try {
        const response = await ForeplayAPI.Brands.getBrandRankedAds(
          this.brand.id,
          this.selectedAdvertiserId,
          category,
          { limit: ADS_PER_PAGE, page }
        )
        if (requestId !== this.latestRequestId) return
        const ads = response?.data ?? []

        this.rankedAds = page > 1 ? [...this.rankedAds, ...ads] : ads
        this.categoryCounts = {
          current: { ...EMPTY_COUNTS, ...(response?.counts ?? {}) },
          previous: { ...EMPTY_COUNTS, ...(response?.previous_counts ?? {}) }
        }

        this.currentPage = page
        this.allAdsLoaded = response?.pages ? page >= response.pages : ads.length < ADS_PER_PAGE
        this.infiniteId++
      } catch (error) {
        if (requestId !== this.latestRequestId) return
        console.error('Error fetching ranked ads:', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to fetch ranked ads. See console for details.'
        })
      } finally {
        if (requestId === this.latestRequestId) {
          this.loadingRankedAds = false
          this.loadingInitialFetch = false
        }
      }
    },
    async getMoreRankedAds () {
      if (this.loadingRankedAds || this.allAdsLoaded || this.locked) return
      await this.fetchRankedAds(this.selectedCategory, this.currentPage + 1)
    },
    async handleChangeCategory (category) {
      if (this.loadingRankedAds || category === this.selectedCategory || this.brand?.is_subscribed) return
      this.selectedCategory = category
      this.currentPage = 1
      this.rankedAds = []
      this.allAdsLoaded = false
      this.infiniteId++
      await this.fetchRankedAds(category)
    },
    updateRankedAdsLocal (adsToUpdate) {
      const duplicateAds = [...this.rankedAds]
      adsToUpdate.forEach((ad) => {
        const index = duplicateAds.findIndex((a) => a.id === ad.id)
        if (index !== -1) {
          duplicateAds[index] = ad
        }
      })
      this.rankedAds = duplicateAds
    },
    resetRankedAdsState () {
      this.selectedCategory = 'all'
      this.rankedAds = []
      this.categoryCounts = {
        current: { ...EMPTY_COUNTS },
        previous: { ...EMPTY_COUNTS }
      }
      this.currentPage = 1
      this.loadingInitialFetch = true
      this.allAdsLoaded = false
      this.infiniteId++
    },
  }
}
</script>

<style scoped>
.category-atom {
  position: relative;
  height: 84px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border-radius: 10px;
  transition: background-color 200ms ease-in-out, box-shadow 200ms ease-in-out;
  box-shadow: none;
  background-color: transparent;
}
.category-atom:not(.enabled) {
  cursor: default;
}
.category-atom.enabled:hover:not(.active) {
  background-color: #ECEFF3;
}
.category-atom.active {
  background-color: transparent;
  pointer-events: none;
}
.category-atom .active-indicator {
  position: absolute;
  top: -6px;
  bottom: -2px;
  left: -32px;
  right: -32px;
  overflow-y: hidden;
  border-radius: 10px;
  pointer-events: none;
}
.category-atom.first .active-indicator { left: -4px; }
.category-atom.last .active-indicator { right: -4px; }
.category-atom .active-indicator .inner {
  position: absolute;
  top: 6px;
  bottom: 2px;
  left: 28px;
  right: 28px;
  border-radius: 10px;
  background-color: transparent;
  box-shadow: none;
  transition: background-color 150ms ease-in-out, box-shadow 150ms ease-in-out;
}
.category-atom.first .active-indicator .inner { left: 4px; }
.category-atom.last .active-indicator .inner { right: 4px; }
.category-atom.active .active-indicator .inner {
  background-color: white;
  box-shadow: 0 160px 64px 0 rgba(46, 53, 73, 0.02), 0 90px 54px -8px rgba(46, 53, 73, 0.04), 0 40px 40px -12px rgba(46, 53, 73, 0.07), 0 10px 22px -8px rgba(46, 53, 73, 0.16);
}

.fade-in {
  animation: fadeIn 150ms ease-in-out forwards;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>